import { useEffect, useMemo, useState } from 'react'
import { loadOperationalIntelligence, type OperationalIntelligence } from '../../lib/intelligence'

const PERIODS = [7, 15, 30] as const

function pctChange(current: number, previous: number) {
  if (previous <= 0) return current > 0 ? null : 0
  return ((current - previous) / previous) * 100
}

function deltaLabel(current: number, previous: number) {
  const value = pctChange(current, previous)
  if (value == null) return 'novo no período'
  const signal = value > 0 ? '+' : ''
  return `${signal}${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% vs. período anterior`
}

function scoreTone(value: number) {
  if (value >= 80) return 'good'
  if (value >= 60) return 'neutral'
  return 'attention'
}

export function OperationalIntelligencePanel() {
  const [days, setDays] = useState<number>(30)
  const [data, setData] = useState<OperationalIntelligence | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError('')
    loadOperationalIntelligence(days)
      .then((result) => mounted && setData(result))
      .catch((err) => mounted && setError(err instanceof Error ? err.message : 'Não foi possível carregar a inteligência operacional.'))
      .finally(() => mounted && setLoading(false))
    return () => { mounted = false }
  }, [days])

  const leastUsed = useMemo(() => [...(data?.module_usage ?? [])].sort((a, b) => a.accesses - b.accesses).slice(0, 5), [data])
  const current = data?.comparison.current
  const previous = data?.comparison.previous

  return (
    <section className="operational-intelligence">
      <div className="admin-section-title usage-heading">
        <div>
          <p className="eyebrow">INTELIGÊNCIA OPERACIONAL</p>
          <h2>Do uso para a melhoria contínua</h2>
          <p className="muted">Cruza unidade, buscas, conteúdo, resolutividade, releases e padrões fora do comportamento habitual.</p>
        </div>
        <div className="usage-periods" aria-label="Período da análise">
          {PERIODS.map((period) => <button key={period} className={days === period ? 'active' : ''} onClick={() => setDays(period)}>{period} dias</button>)}
        </div>
      </div>

      {loading && <div className="empty-state">Carregando inteligência operacional...</div>}
      {error && <div className="notice error">{error}</div>}

      {!loading && data && current && previous && (
        <>
          <div className="usage-kpi-grid intelligence-kpis">
            <article><span>Sessões</span><strong>{current.sessions}</strong><small>{deltaLabel(current.sessions, previous.sessions)}</small></article>
            <article><span>Utilizações de módulos</span><strong>{current.module_views}</strong><small>{deltaLabel(current.module_views, previous.module_views)}</small></article>
            <article><span>Buscas globais</span><strong>{current.searches}</strong><small>{current.no_result_searches} sem resultado · {deltaLabel(current.searches, previous.searches)}</small></article>
            <article><span>Resolutividade percebida</span><strong>{Number(current.resolution_pct).toLocaleString('pt-BR')}%</strong><small>{current.feedback_total} respostas no período</small></article>
          </div>

          <section className="usage-panel">
            <div className="usage-panel-head"><div><p className="eyebrow">1 · USO POR UNIDADE</p><h3>Onde o Suite é utilizado</h3></div><span>{data.units.length} contextos</span></div>
            <div className="intelligence-table-wrap">
              <table className="intelligence-table"><thead><tr><th>Unidade</th><th>Sessões</th><th>Usuários</th><th>Utilizações</th><th>Buscas</th><th>Sem resposta</th><th>Resolveu</th></tr></thead><tbody>
                {data.units.map((unit) => <tr key={unit.unit_key}><td><strong>{unit.unit_label}</strong></td><td>{unit.sessions}</td><td>{unit.unit_key === 'call-center' ? 'compartilhado' : unit.unique_users}</td><td>{unit.module_views}</td><td>{unit.searches}</td><td>{unit.no_result_searches}</td><td><span className={`intelligence-score ${scoreTone(unit.resolution_pct)}`}>{Number(unit.resolution_pct).toLocaleString('pt-BR')}%</span></td></tr>)}
              </tbody></table>
            </div>
          </section>

          <div className="usage-grid-two">
            <section className="usage-panel">
              <div className="usage-panel-head"><div><p className="eyebrow">2 · INTELIGÊNCIA DE BUSCA</p><h3>O que mais procuram</h3></div><span>{days} dias</span></div>
              <div className="usage-ranking-list">
                {data.top_searches.length === 0 && <div className="empty-state">As buscas começarão a aparecer após a nova coleta.</div>}
                {data.top_searches.slice(0, 10).map((item, index) => <article key={`${item.query_text}-${index}`}><b className="usage-rank">{index + 1}</b><div><strong>{item.query_text}</strong><small>{item.selections} escolhas · média {item.avg_results} resultados</small></div><em>{item.searches}</em></article>)}
              </div>
            </section>

            <section className="usage-panel intelligence-gap-panel">
              <div className="usage-panel-head"><div><p className="eyebrow">3 · LACUNAS DE CONTEÚDO</p><h3>Buscas sem resultado</h3></div><span>prioridade</span></div>
              <div className="usage-ranking-list">
                {data.no_result_searches.length === 0 && <div className="empty-state">Nenhuma lacuna registrada no período.</div>}
                {data.no_result_searches.slice(0, 10).map((item, index) => <article key={`${item.query_text}-${index}`}><b className="usage-rank">{index + 1}</b><div><strong>{item.query_text}</strong><small>{item.users} usuários · investigar necessidade de conteúdo</small></div><em>{item.searches}</em></article>)}
              </div>
            </section>
          </div>

          <div className="usage-grid-two">
            <section className="usage-panel">
              <div className="usage-panel-head"><div><p className="eyebrow">5 · COMPARATIVO</p><h3>Período atual x anterior</h3></div><span>{days} dias</span></div>
              <div className="intelligence-comparison-grid">
                <article><span>Usuários únicos</span><strong>{current.unique_users}</strong><small>{deltaLabel(current.unique_users, previous.unique_users)}</small></article>
                <article><span>Buscas sem resultado</span><strong>{current.no_result_searches}</strong><small>{deltaLabel(current.no_result_searches, previous.no_result_searches)}</small></article>
                <article><span>Resolutividade</span><strong>{Number(current.resolution_pct).toLocaleString('pt-BR')}%</strong><small>antes {Number(previous.resolution_pct).toLocaleString('pt-BR')}%</small></article>
              </div>
            </section>

            <section className="usage-panel">
              <div className="usage-panel-head"><div><p className="eyebrow">6 · CONTEÚDO</p><h3>Mais e menos utilizados</h3></div><span>{data.module_usage.length} módulos</span></div>
              <div className="intelligence-content-columns">
                <div><h4>Mais utilizados</h4>{data.module_usage.slice(0, 5).map((item, index) => <p key={item.module_key}><b>{index + 1}</b><span>{item.module_label}</span><strong>{item.accesses}</strong></p>)}</div>
                <div><h4>Menos utilizados</h4>{leastUsed.map((item, index) => <p key={item.module_key}><b>{index + 1}</b><span>{item.module_label}</span><strong>{item.accesses}</strong></p>)}</div>
              </div>
            </section>
          </div>

          <div className="usage-grid-two">
            <section className="usage-panel">
              <div className="usage-panel-head"><div><p className="eyebrow">7 · RESOLUTIVIDADE</p><h3>“Isso ajudou?”</h3></div><span>{current.feedback_total} respostas</span></div>
              <div className="usage-ranking-list">
                {data.feedback.length === 0 && <div className="empty-state">O feedback contextual começará a gerar indicadores após as respostas dos operadores.</div>}
                {data.feedback.slice(0, 10).map((item, index) => <article key={item.target_key}><b className="usage-rank">{index + 1}</b><div><strong>{item.target_label}</strong><small>{item.resolved_yes} “sim” em {item.responses} respostas</small></div><em>{Number(item.resolution_pct).toLocaleString('pt-BR')}%</em></article>)}
              </div>
            </section>

            <section className="usage-panel">
              <div className="usage-panel-head"><div><p className="eyebrow">CONTEÚDO ESCOLHIDO NA BUSCA</p><h3>Resultados mais abertos</h3></div><span>{days} dias</span></div>
              <div className="usage-ranking-list">
                {data.selected_content.length === 0 && <div className="empty-state">Ainda não há escolhas de resultados registradas.</div>}
                {data.selected_content.slice(0, 10).map((item, index) => <article key={`${item.selected_key}-${index}`}><b className="usage-rank">{index + 1}</b><div><strong>{item.selected_label}</strong><small>{item.users} usuários</small></div><em>{item.selections}</em></article>)}
              </div>
            </section>
          </div>

          <section className="usage-panel">
            <div className="usage-panel-head"><div><p className="eyebrow">8 · IMPACTO DE RELEASES</p><h3>Uso antes e depois da publicação</h3></div><span>média diária</span></div>
            <div className="release-impact-grid">
              {data.release_impact.length === 0 && <div className="empty-state">Nenhuma release publicada disponível para comparação.</div>}
              {data.release_impact.map((release) => {
                const change = pctChange(Number(release.after_daily_avg), Number(release.before_daily_avg))
                return <article key={release.release_id}><span>v{release.version}</span><strong>{release.titulo}</strong><small>Antes: {release.before_daily_avg}/dia · Depois: {release.after_daily_avg}/dia · {release.days_after} dia(s) observados</small><em>{change == null ? 'base nova' : `${change > 0 ? '+' : ''}${change.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`}</em></article>
              })}
            </div>
          </section>

          <section className="usage-panel intelligence-alerts">
            <div className="usage-panel-head"><div><p className="eyebrow">9 · DETECÇÃO DE ANOMALIAS</p><h3>Comportamentos fora do padrão</h3></div><span>hoje x 7 dias</span></div>
            {data.anomalies.length === 0 ? <div className="empty-state">Nenhum aumento fora do padrão foi detectado hoje.</div> : <div className="intelligence-alert-list">{data.anomalies.map((item) => <article key={item.module_key}><span>⚠</span><div><strong>{item.module_label}</strong><small>Hoje: {item.today_accesses} · média anterior: {item.baseline_daily_avg}/dia</small></div><em>{item.change_pct == null ? 'novo pico' : `+${Number(item.change_pct).toLocaleString('pt-BR')}%`}</em></article>)}</div>}
          </section>
        </>
      )}
    </section>
  )
}
